import { Link } from "react-router-dom";
import LegalFooter from "../components/LegalFooter";
import ComplianceBadge from "../components/ComplianceBadge";
import {
  CalendarDays, FileText, Globe2, Sparkles, ArrowRight, ShieldCheck,
  Stethoscope, Pill, Activity, MessageSquare, Heart, FileSignature, Compass, FlaskConical,
  Award, Briefcase, GraduationCap, CheckCircle2
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";

export default function Landing() {
  const { t } = useI18n();
  const features = [
    { icon: CalendarDays, t: t("feature1_t"), d: t("feature1_d") },
    { icon: FileText, t: t("feature3_t"), d: t("feature3_d") },
    { icon: Globe2, t: t("feature4_t"), d: t("feature4_d") },
    { icon: Sparkles, t: t("feature2_t"), d: t("feature2_d") },
  ];
  const services = [
    { icon: Stethoscope, t: t("svc1_t"), d: t("svc1_d") },
    { icon: Pill, t: t("svc2_t"), d: t("svc2_d") },
    { icon: Activity, t: t("svc3_t"), d: t("svc3_d") },
    { icon: MessageSquare, t: t("svc4_t"), d: t("svc4_d") },
    { icon: Heart, t: t("svc5_t"), d: t("svc5_d") },
    { icon: FileSignature, t: t("svc6_t"), d: t("svc6_d") },
    { icon: Compass, t: t("svc7_t"), d: t("svc7_d") },
    { icon: FlaskConical, t: "Análisis e imagen", d: "Peticiones de laboratorio y estudios de imagen con PDF firmado y QR." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size={36} withText />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/chat" className="hidden md:inline-flex" data-testid="nav-public-chat">
              <Button variant="ghost" className="gap-2"><Sparkles className="w-4 h-4" /> Chat IA gratis</Button>
            </Link>
            <Link to="/login"><Button variant="ghost" data-testid="nav-login">{t("login")}</Button></Link>
            <Link to="/register"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full" data-testid="nav-register">{t("cta_book")}</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F3F1EC] border border-border text-sm text-muted-foreground mb-6">
            <ShieldCheck className="w-4 h-4 text-primary" /> {t("tagline")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-foreground">
            Dr. Dimy Laguerre
          </h1>
          <p className="mt-3 text-xl text-primary font-medium">Médico Especialista en Medicina de Urgencias y Emergencias</p>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
            Atención médica privada, urgencias extrahospitalarias y medicina laboral.
            Receta electrónica REMPE y teleconsulta segura.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Urgencias", "Atención primaria", "Medicina laboral", "Receta electrónica REMPE"].map((tag) => (
              <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-4">
            <ComplianceBadge variant="compact" />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="bg-primary hover:bg-primary/90 rounded-full px-7" data-testid="hero-cta-book">
                Reservar consulta <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-cta-login">{t("cta_login")}</Button>
            </Link>
          </div>
        </div>
        <div className="relative fade-up">
          <div className="absolute -inset-4 bg-gradient-to-br from-secondary/30 to-primary/20 rounded-[2rem] blur-2xl" />
          <img
            src="/dr-dimy-photo.jpeg"
            alt="Dr. Dimy Laguerre"
            className="relative rounded-[2rem] shadow-[0_20px_60px_-20px_rgba(54,74,54,0.4)] object-cover w-full h-[520px]"
            data-testid="hero-doctor-photo"
          />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-border max-w-[240px]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-display text-sm font-semibold">Desde 2020</div>
                <div className="text-xs text-muted-foreground">Trayectoria consolidada</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre el doctor */}
      <section className="max-w-7xl mx-auto px-6 pb-16" id="sobre-mi">
        <div className="bg-gradient-to-br from-[#F3F1EC] to-white border border-border rounded-[2rem] p-8 lg:p-12">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm text-primary mb-4">
                <Stethoscope className="w-4 h-4" /> Sobre el Dr. Dimy
              </div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">Trayectoria & especialización</h2>
              <p className="text-muted-foreground leading-relaxed">
                Médico con enfoque integrador: respuesta inmediata en urgencias, promoción de la salud y seguridad laboral.
              </p>
              <div className="mt-6 space-y-3" data-testid="doctor-credentials">
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-sm">Servicio Andaluz de Salud (SAS)</div>
                    <div className="text-xs text-muted-foreground">Urgencias de Atención Primaria — SUAP</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-sm">Laboral Group</div>
                    <div className="text-xs text-muted-foreground">Médico reconocedor · Medicina preventiva</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <GraduationCap className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-sm">Médico Especialista en Medicina de Urgencias y Emergencias</div>
                    <div className="text-xs text-muted-foreground">Nº Colegiado 41/4119650 · Receta REMPE</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-display text-xl mb-4">Biografía</h3>
              <div className="prose prose-sm max-w-none text-foreground/85 leading-relaxed space-y-4">
                <p>
                  Médico Especialista en Medicina de Urgencias y Emergencias con <strong>trayectoria consolidada desde 2020</strong> en atención primaria
                  y medicina crítica. Especializado en la gestión eficiente de pacientes en entornos de alta
                  complejidad, con experiencia en <strong>urgencias extrahospitalarias</strong>.
                </p>
                <p>
                  Actualmente desempeña su actividad asistencial en el <strong>Servicio de Urgencias de
                  Atención Primaria (SUAP)</strong> del Servicio Andaluz de Salud (SAS), labor que compatibiliza
                  con la <strong>medicina preventiva y la vigilancia de la salud</strong> como médico reconocedor
                  en Laboral Group.
                </p>
                <p>
                  Su enfoque profesional integra la respuesta inmediata en urgencias con la promoción de la
                  salud y la seguridad en el ámbito laboral.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  { v: "5+", l: "años de trayectoria" },
                  { v: "3", l: "idiomas ES · FR · HT" },
                  { v: "24/7", l: "verificación QR" },
                ].map((s) => (
                  <div key={s.l} className="bg-white border border-border rounded-xl p-4 text-center">
                    <div className="font-display text-2xl text-primary">{s.v}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="card-lift bg-card border border-border rounded-xl p-6" data-testid={`feature-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#F3F1EC] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg font-medium mb-2">{f.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" id="servicios">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/15 border border-secondary/30 text-sm text-secondary mb-4">
            <Stethoscope className="w-4 h-4" /> Servicios médicos
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{t("services_title")}</h2>
          <p className="text-muted-foreground mt-3">{t("services_subtitle")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="card-lift bg-card border border-border rounded-xl p-6" data-testid={`service-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-base font-medium mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Por qué elegir al Dr. Dimy */}
      <section className="bg-gradient-to-b from-white to-[#F3F1EC] border-y border-border py-20" id="por-que">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm text-primary mb-4">
              <CheckCircle2 className="w-4 h-4" /> Por qué elegir esta consulta
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Cuidado profesional, digital y cercano</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { i: ShieldCheck, t: "Receta REMPE válida", d: "Recetas electrónicas con QR de verificación pública. Cumple normativa española.", c: "emerald" },
              { i: Globe2, t: "3 idiomas", d: "Atención en español, francés y kreyòl ayisyen — accesible globalmente.", c: "blue" },
              { i: Sparkles, t: "Resumen automático", d: "Tras cada teleconsulta recibes por email tus recetas, exámenes y notas.", c: "violet" },
              { i: Activity, t: "Teleconsulta (videoconsulta + llamada)", d: "Videoconsulta segura por Jitsi Meet o llamada telefónica.", c: "amber" },
              { i: FileSignature, t: "Pagos seguros", d: "Stripe (tarjeta) o Wise. Confirmación inmediata y factura digital.", c: "rose" },
              { i: MessageSquare, t: "Chat con IA gratis", d: "Consulta inicial gratuita 24/7 con asistente médico IA antes de reservar.", c: "indigo" },
            ].map((p, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-6 card-lift" data-testid={`benefit-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <p.i className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{p.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-[2rem] p-10 lg:p-14 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-secondary/30 rounded-full blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
              Reserva tu consulta con el Dr. Dimy
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-2xl mx-auto">
              Atención médica privada con receta electrónica REMPE, verificación QR y resumen automático tras cada consulta.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white hover:bg-white/95 text-primary rounded-full px-8 font-semibold" data-testid="cta-final-book">
                  Reservar ahora <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/chat">
                <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full px-8" data-testid="cta-final-chat">
                  Probar chat IA gratis
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 justify-center text-sm text-white/70">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Sin compromiso</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Receta REMPE válida</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Pago seguro</span>
            </div>
            <div className="mt-6 flex justify-center">
              <ComplianceBadge variant="compact" dark />
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-white py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-display text-xl mb-2">Dr. Dimy Laguerre</div>
            <p className="text-sm text-white/60 leading-relaxed">
              Consulta médica privada con receta electrónica REMPE.
            </p>
            <p className="text-xs text-white/40 mt-3">Nº Colegiado 41/4119650 · Colegio Oficial de Médicos de Sevilla</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-3">Contacto</div>
            <div className="text-sm space-y-2 text-white/80">
              <div>📧 djimlag25@gmail.com</div>
              <div>📱 +34 742 08 65 96</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-3">Acceso</div>
            <div className="text-sm space-y-2">
              <Link to="/register" className="block text-white/80 hover:text-white">Reservar consulta</Link>
              <Link to="/login" className="block text-white/80 hover:text-white">Iniciar sesión</Link>
              <Link to="/chat" className="block text-white/80 hover:text-white">Chat IA gratis</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-6 max-w-7xl mx-auto px-6">
          <div className="mb-6 max-w-2xl mx-auto">
            <ComplianceBadge variant="full" dark />
          </div>
          <LegalFooter dark />
          <div className="text-center text-xs text-white/40 mt-4">
            © {new Date().getFullYear()} Dr. Dimy Laguerre · Receta REMPE · Servicio sujeto al RGPD y LOPDGDD
          </div>
        </div>
      </footer>

      <Link
        to="/chat"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-secondary hover:bg-secondary/90 text-white shadow-[0_10px_30px_-8px_rgba(0,0,0,0.3)] transition-transform hover:scale-105"
        data-testid="floating-public-chat"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">Chat médico gratis</span>
      </Link>
    </div>
  );
}
